import { createSlice } from "@reduxjs/toolkit";
import { IDistributorState } from "./distributors.interfaces";
import {
  addDistributor,
  deleteDistributor,
  getDistributor,
  getDistributors,
  updateDistributor,
} from "./distributors.actions";

const initialState: IDistributorState = {
  selectedDistributor: null,
  selectedDistributors: [],
  addDistributorModal: false,

  // Get Distributors
  isDistributorsLoading: false,
  distributorsError: "",
  distributors: [],
  pagination: {
    page: 0,
    size: 10,
    length: 0,
  },
  filters: {
    search: "",
    sort: "",
    order: "",
    page: 0,
    limit: 10,
  },

  // Get Distributor
  isGetDistributorLoading: false,
  getDistributorError: "",
  distributor: null,

  // Add Distributor
  isAddDistributorLoading: false,
  addDistributorError: "",
  addDistributorSuccess: false,

  // Update Distributor
  isUpdateDistributorLoading: false,
  updateDistributorError: "",
  updateDistributorSuccess: false,

  // Delete Distributor
  isDeleteDistributorLoading: false,
  deleteDistributorError: "",
  deleteDistributorSuccess: false,
};

const distributorsSlice = createSlice({
  name: "distributors",
  initialState,
  reducers: {
    resetGetDistributors: (state) => {
      state.distributors = [];
      state.pagination = {
        page: 0,
        size: 10,
        length: 0,
      };
      state.filters = {
        search: "",
        sort: "",
        order: "",
        page: 0,
        limit: 10,
      };
    },
    setAddDistributorModal: (state, action) => {
      state.addDistributorModal = action.payload;
    },
    resetAddDistributor: (state) => {
      state.addDistributorError = "";
      state.addDistributorSuccess = false;
      state.isAddDistributorLoading = false;
    },
    resetUpdateDistributor: (state) => {
      state.updateDistributorError = "";
      state.updateDistributorSuccess = false;
      state.isUpdateDistributorLoading = false;
    },
    resetDeleteDistributor: (state) => {
      state.deleteDistributorError = "";
      state.deleteDistributorSuccess = false;
      state.isDeleteDistributorLoading = false;
    },
    setSelectedDistributor: (state, action) => {
      state.selectedDistributor = action.payload;
    },
    setSelectedDistributors: (state, action) => {
      state.selectedDistributors = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetGetDistributor: (state) => {
      state.distributor = null;
      state.isGetDistributorLoading = false;
      state.getDistributorError = "";
    },
  },
  extraReducers: (builder) => {
    // Get Distributors
    builder
      .addCase(getDistributors.pending, (state) => {
        state.isDistributorsLoading = true;
      })
      .addCase(getDistributors.fulfilled, (state, action) => {
        state.isDistributorsLoading = false;
        state.distributors = action.payload.distributors;
        state.pagination = action.payload.pagination;
      })
      .addCase(getDistributors.rejected, (state, action) => {
        state.isDistributorsLoading = false;
        state.distributorsError =
          action.payload || "Failed to fetch distributors";
      });

    // Get Distributor
    builder
      .addCase(getDistributor.pending, (state) => {
        state.isGetDistributorLoading = true;
      })
      .addCase(getDistributor.fulfilled, (state, action) => {
        state.isGetDistributorLoading = false;
        state.selectedDistributor = action.payload;
      })
      .addCase(getDistributor.rejected, (state, action) => {
        state.isGetDistributorLoading = false;
        state.getDistributorError =
          action.payload || "Failed to fetch distributor";
      });

    // Add Distributor
    builder
      .addCase(addDistributor.pending, (state) => {
        state.isAddDistributorLoading = true;
      })
      .addCase(addDistributor.fulfilled, (state) => {
        state.isAddDistributorLoading = false;
        state.addDistributorSuccess = true;
      })
      .addCase(addDistributor.rejected, (state, action) => {
        state.isAddDistributorLoading = false;
        state.addDistributorError =
          action.payload || "Failed to add distributor";
      });

    // Update Distributor
    builder
      .addCase(updateDistributor.pending, (state) => {
        state.isUpdateDistributorLoading = true;
      })
      .addCase(updateDistributor.fulfilled, (state) => {
        state.isUpdateDistributorLoading = false;
        state.updateDistributorSuccess = true;
      })
      .addCase(updateDistributor.rejected, (state, action) => {
        state.isUpdateDistributorLoading = false;
        state.updateDistributorError =
          action.payload || "Failed to update distributor";
      });

    // Delete Distributor
    builder
      .addCase(deleteDistributor.pending, (state) => {
        state.isDeleteDistributorLoading = true;
      })
      .addCase(deleteDistributor.fulfilled, (state) => {
        state.isDeleteDistributorLoading = false;
        state.deleteDistributorSuccess = true;
      })
      .addCase(deleteDistributor.rejected, (state, action) => {
        state.isDeleteDistributorLoading = false;
        state.deleteDistributorError =
          action.payload || "Failed to delete distributor";
      });
  },
});

export default distributorsSlice.reducer;

export const {
  resetGetDistributors,
  setAddDistributorModal,
  resetAddDistributor,
  resetUpdateDistributor,
  resetDeleteDistributor,
  setSelectedDistributor,
  setSelectedDistributors,
  setFilters,
  resetGetDistributor,
} = distributorsSlice.actions;
